export interface NavigationItem {
  id: string;
  path?: string;
  titleId: string;
  icon: string;
  planned?: boolean;
  /** 平台权限 `资源:操作`，见 src/permissions/definitions.ts */
  access?: string;
  /** Group entries have no route of their own and expand into nested children. */
  children?: NavigationItem[];
}

export type NavigationAccessChecker =
  | ((accessCode: string) => boolean)
  | Record<string, boolean | undefined>
  | undefined;

export function hasNavigationAccess(
  item: NavigationItem,
  access?: NavigationAccessChecker,
): boolean {
  if (!item.access) return true;
  if (!access) return false;
  if (typeof access === 'function') {
    return Boolean(access(item.access));
  }
  return Boolean(access[item.access]);
}

export interface NavigationDomain {
  id: string;
  path: string;
  titleId: string;
  icon: string;
  items: NavigationItem[];
}

/** 第一个可进入的菜单（含嵌套分组里的子菜单）；不传 access 时只看是否可导航 */
const findFirstLeaf = (
  items: NavigationItem[],
  canEnter: (item: NavigationItem) => boolean,
): NavigationItem | undefined => {
  for (const item of items) {
    if (item.planned) continue;
    if (item.path && canEnter(item)) return item;
    const nested = findFirstLeaf(item.children ?? [], canEnter);
    if (nested) return nested;
  }
  return undefined;
};

/** 模块内至少有一个可进入的菜单（含嵌套分组）时才显示该模块 */
export function hasDomainAccess(
  domain: NavigationDomain,
  access?: NavigationAccessChecker,
): boolean {
  return Boolean(
    findFirstLeaf(domain.items, (item) => hasNavigationAccess(item, access)),
  );
}

/**
 * Navigation groups are independent of route URLs so existing links stay valid.
 *
 * 菜单按平台权限（`资源:操作`）控制，模块里没有可进入的菜单时整体隐藏，
 * 避免看到点进去就 403 的入口。
 */
export const navigationDomains: NavigationDomain[] = [
  {
    id: 'workspace',
    path: '/home',
    titleId: 'menu.centers.workspace',
    icon: 'home',
    items: [
      {
        id: 'workspaceOverview',
        path: '/home',
        titleId: 'pages.navigation.items.workspaceOverview',
        icon: 'dashboard',
      },
    ],
  },
  {
    id: 'applications',
    path: '/application-center',
    titleId: 'menu.centers.applications',
    icon: 'appstore',
    items: [
      {
        id: 'applicationManagement',
        titleId: 'pages.navigation.items.applicationManagement',
        icon: 'appstore',
        children: [
          {
            id: 'applications',
            path: '/platform-management/applications',
            titleId: 'pages.navigation.items.applications',
            icon: 'appstore',
            access: 'applications:read',
          },
          {
            id: 'pluginApplications',
            path: '/platform-management/plugin-applications',
            titleId: 'pages.navigation.items.pluginApplications',
            icon: 'block',
            access: 'applications:read',
          },
        ],
      },
      {
        id: 'applicationFeedback',
        path: '/platform-management/application-feedback',
        titleId: 'pages.navigation.items.applicationFeedback',
        icon: 'message',
      },
      {
        id: 'externalPublication',
        path: '/platform-management/external-application-publication',
        titleId: 'pages.navigation.items.externalPublication',
        icon: 'upload',
        access: 'publications:read',
      },
      {
        id: 'publicationReview',
        path: '/platform-management/application-publication-review',
        titleId: 'pages.navigation.items.publicationReview',
        icon: 'audit',
        access: 'publications:read',
      },
    ],
  },
  {
    id: 'capabilities',
    path: '/service',
    titleId: 'menu.centers.capabilities',
    icon: 'thunderbolt',
    items: [
      {
        id: 'events',
        path: '/service/events',
        titleId: 'pages.navigation.items.events',
        icon: 'deploymentUnit',
        access: 'events:read',
      },
      {
        id: 'storage',
        path: '/service/application-storage',
        titleId: 'pages.navigation.items.storage',
        icon: 'hdd',
        access: 'storage:read',
      },
      {
        id: 'models',
        titleId: 'menu.centers.aiModels',
        icon: 'robot',
        children: [
          {
            id: 'aiScenes',
            path: '/service/ai-runtime/scenes',
            titleId: 'pages.navigation.items.aiScenes',
            icon: 'appstore',
            access: 'llm:read',
          },
          {
            id: 'modelAccess',
            path: '/service/ai-runtime/model-access',
            titleId: 'pages.navigation.items.modelAccess',
            icon: 'api',
            access: 'llm:read',
          },
          {
            id: 'modelPolicies',
            path: '/service/ai-runtime/runtime-policies',
            titleId: 'pages.navigation.items.modelPolicies',
            icon: 'control',
            access: 'llm:read',
          },
          {
            id: 'modelCircuits',
            path: '/service/ai-runtime/circuit-breakers',
            titleId: 'pages.navigation.items.modelCircuits',
            icon: 'safetyCertificate',
            access: 'llm:read',
          },
          {
            id: 'modelCallLogs',
            path: '/service/ai-runtime/call-logs',
            titleId: 'pages.navigation.items.modelCallLogs',
            icon: 'fileText',
            access: 'llm:read',
          },
          {
            id: 'modelUsage',
            path: '/service/ai-runtime/usage',
            titleId: 'pages.navigation.items.modelUsage',
            icon: 'dashboard',
            access: 'llm:read',
          },
          {
            id: 'modelTenantUsage',
            path: '/service/ai-runtime/tenant-usage',
            titleId: 'pages.navigation.items.modelTenantUsage',
            icon: 'team',
            access: 'llm:read',
          },
        ],
      },
      {
        id: 'skillMarket',
        path: '/service/ai-market/skills',
        titleId: 'pages.navigation.items.skillMarket',
        icon: 'tool',
        access: 'ai_market:read',
      },
      {
        id: 'mcpMarket',
        path: '/service/ai-market/mcp',
        titleId: 'pages.navigation.items.mcpMarket',
        icon: 'api',
        access: 'ai_market:read',
      },
      {
        id: 'digitalHumanMarket',
        path: '/service/ai-market/digital-humans',
        titleId: 'pages.navigation.items.digitalHumanMarket',
        icon: 'robot',
        access: 'ai_market:read',
      },
      {
        id: 'knowledge',
        titleId: 'pages.navigation.items.knowledge',
        icon: 'read',
        planned: true,
      },
      {
        id: 'dataSources',
        titleId: 'pages.navigation.items.dataSources',
        icon: 'database',
        planned: true,
      },
    ],
  },
  {
    // 市场对所有登录用户开放，不挂平台权限
    id: 'markets',
    path: '/markets',
    titleId: 'menu.centers.markets',
    icon: 'shop',
    items: [
      {
        id: 'appMarket',
        path: '/markets/apps',
        titleId: 'pages.navigation.items.appMarket',
        icon: 'appstore',
      },
      {
        id: 'pluginMarketplace',
        path: '/markets/plugins',
        titleId: 'pages.navigation.items.pluginMarketplace',
        icon: 'block',
      },
      {
        id: 'skillMarketplace',
        path: '/markets/skills',
        titleId: 'pages.navigation.items.skillMarketplace',
        icon: 'tool',
      },
      {
        id: 'mcpMarketplace',
        path: '/markets/mcp',
        titleId: 'pages.navigation.items.mcpMarketplace',
        icon: 'api',
      },
      {
        id: 'digitalHumanMarketplace',
        path: '/markets/digital-humans',
        titleId: 'pages.navigation.items.digitalHumanMarketplace',
        icon: 'robot',
      },
    ],
  },
  {
    id: 'resources',
    path: '/resource-center',
    titleId: 'menu.centers.resources',
    icon: 'cloudServer',
    items: [
      {
        id: 'resourceOverview',
        path: '/resource-center/overview',
        titleId: 'pages.navigation.items.resourceOverview',
        icon: 'dashboard',
        access: 'clusters:read',
      },
      {
        id: 'clusters',
        path: '/platform-management/clusters',
        titleId: 'pages.navigation.items.clusters',
        icon: 'cloudServer',
        access: 'clusters:read',
      },
      {
        id: 'appStore',
        path: '/resource-center/app-store',
        titleId: 'pages.navigation.items.appStore',
        icon: 'shop',
        access: 'deployments:read',
      },
      {
        id: 'deployments',
        path: '/resource-center/deployments',
        titleId: 'pages.navigation.items.deployments',
        icon: 'rocket',
        access: 'deployments:read',
      },
      {
        id: 'images',
        path: '/resource-center/images',
        titleId: 'pages.navigation.items.images',
        icon: 'block',
        access: 'images:read',
      },
    ],
  },
  {
    id: 'commercial',
    path: '/commercial-center',
    titleId: 'menu.centers.commercial',
    icon: 'dollar',
    items: [
      {
        id: 'products',
        path: '/platform-management/product-catalog',
        titleId: 'pages.navigation.items.products',
        icon: 'shopping',
        access: 'commercial:read',
      },
      {
        id: 'packages',
        path: '/platform-management/tenant-packages',
        titleId: 'pages.navigation.items.packages',
        icon: 'gift',
        access: 'commercial:read',
      },
      {
        id: 'orders',
        path: '/platform-management/payment-orders',
        titleId: 'pages.navigation.items.orders',
        icon: 'creditCard',
        access: 'orders:read',
      },
    ],
  },
  {
    id: 'management',
    path: '/platform-center',
    titleId: 'menu.centers.management',
    icon: 'bank',
    items: [
      {
        id: 'platformTenants',
        path: '/management/tenants',
        titleId: 'pages.navigation.items.platformTenants',
        icon: 'global',
        access: 'tenants:read',
      },
      {
        id: 'platformUsers',
        path: '/management/users',
        titleId: 'pages.navigation.items.platformUsers',
        icon: 'user',
        access: 'users:read',
      },
    ],
  },
  {
    id: 'settings',
    path: '/platform-settings',
    titleId: 'menu.centers.settings',
    icon: 'setting',
    items: [
      {
        id: 'taxonomies',
        path: '/platform-management/taxonomies',
        titleId: 'pages.navigation.items.taxonomies',
        icon: 'tags',
        access: 'taxonomies:read',
      },
      {
        id: 'platformPermissions',
        path: '/platform-settings/permissions',
        titleId: 'pages.navigation.items.platformPermissions',
        icon: 'safetyCertificate',
        access: 'platform_permissions:read',
      },
      {
        id: 'platformSettings',
        path: '/platform-settings',
        titleId: 'pages.navigation.items.platformSettings',
        icon: 'setting',
        access: 'platform_settings:read',
      },
    ],
  },
];

export function getDomainEntryPath(
  domain: NavigationDomain,
  access?: NavigationAccessChecker,
): string {
  const accessible = findFirstLeaf(domain.items, (item) =>
    hasNavigationAccess(item, access),
  );
  if (accessible?.path) return accessible.path;
  // 没有可访问入口时回退到第一个常规入口，避免中心重定向到自身；
  // 具体页面的访问控制仍由路由 access 校验。
  const fallback = findFirstLeaf(domain.items, () => true);
  return fallback?.path || domain.path;
}

const findNavigationDomain = (id: string): NavigationDomain => {
  const domain = navigationDomains.find((item) => item.id === id);
  if (!domain) throw new Error(`Unknown navigation domain: ${id}`);
  return domain;
};

const matchesPath = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

const matchesDestination = (pathname: string, search: string, path: string) => {
  const [destinationPath, destinationSearch] = path.split('?');
  if (!matchesPath(pathname, destinationPath)) return false;
  if (!destinationSearch) return true;
  const params = new URLSearchParams(search);
  return Array.from(new URLSearchParams(destinationSearch)).every(
    ([key, value]) => params.get(key) === value,
  );
};

const findNavigationItemByDestination = (
  items: NavigationItem[],
  pathname: string,
  search: string,
): NavigationItem | undefined => {
  for (const item of items) {
    if (item.path && matchesDestination(pathname, search, item.path)) {
      return item;
    }
    const nested = findNavigationItemByDestination(
      item.children ?? [],
      pathname,
      search,
    );
    if (nested) return nested;
  }
  return undefined;
};

export function getNavigationDomain(location: string): NavigationDomain {
  const [pathname, search = ''] = location.split('#')[0].split('?');
  const domain = navigationDomains.find(
    (item) =>
      matchesPath(pathname, item.path) ||
      Boolean(findNavigationItemByDestination(item.items, pathname, search)),
  );
  if (domain) return domain;
  if (
    matchesPath(pathname, '/platform-management') ||
    matchesPath(pathname, '/management')
  ) {
    return findNavigationDomain('management');
  }
  return findNavigationDomain('workspace');
}

export function getNavigationItem(
  location: string,
): NavigationItem | undefined {
  const domain = getNavigationDomain(location);
  const [pathname, search = ''] = location.split('#')[0].split('?');
  return findNavigationItemByDestination(domain.items, pathname, search);
}
